import { useState, useEffect } from "react";
import Image from "@/components/image";
import { cn } from "@/lib/utils";

type ExpandableWord = {
  id: string;
  word: string;
  content: {
    text: string;
    imageUrl?: string;
    imageWidth?: number;
    imageHeight?: number;
  };
};

interface ExpandableTextProps {
  // Either one paragraph per array entry, or a single string split on blank lines.
  text: string | string[];
  expandableWords: ExpandableWord[];
  className?: string;
}

export function ExpandableText({
  text,
  expandableWords,
  className,
}: ExpandableTextProps) {
  const [expandedWords, setExpandedWords] = useState<string[]>([]);
  const [imageDimensions, setImageDimensions] = useState<
    Record<string, { width: number; height: number }>
  >({});
  const [, setProcessedWords] = useState<Set<string>>(new Set());

  // Pre-calculate image dimensions for all expandable words
  useEffect(() => {
    const dimensions: Record<string, { width: number; height: number }> = {};

    expandableWords.forEach((item) => {
      if (item.content.imageUrl) {
        const width = item.content.imageWidth
          ? Math.min(item.content.imageWidth, 600)
          : 180;
        const height = item.content.imageHeight
          ? Math.min(item.content.imageHeight, 600)
          : 120;
        dimensions[item.id] = { width, height };
      }
    });

    setImageDimensions(dimensions);
  }, [expandableWords]);

  // Reset processedWords when expandedWords changes
  useEffect(() => {
    setProcessedWords(new Set());
  }, [expandedWords]);

  const toggleWord = (id: string) => {
    setExpandedWords((prev) =>
      prev.includes(id) ? prev.filter((wordId) => wordId !== id) : [...prev, id]
    );
  };

  // Add a function to parse the special link syntax
  // Add this function before the renderText function

  const parseLinks = (text: string) => {
    // Regular expression to match <LINK>url</LINK>text<LINK>
    const linkRegex = /<LINK>(.*?)<\/LINK>(.*?)<LINK>/g;

    // Replace all matches with link elements
    return text.replace(linkRegex, (match, url, linkText) => {
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-black underline hover:text-gray-700">${linkText}</a>`;
    });
  };

  // Modify the renderText function to track processed words and only make the first occurrence expandable

  // Update the renderText function to track which words have been processed
  const renderText = () => {
    // Reset processed words for each render
    const processedWordsInThisRender = new Set<string>();

    const paragraphs = (Array.isArray(text) ? text : text.split("\n\n")).map(
      (paragraph) => paragraph.trim()
    );

    return paragraphs.map((paragraph, paragraphIndex) => {
      const result = [];
      let lastIndex = 0;
      let skipPunctuation = false;

      // Sort expandable words by their position in the text
      const sortedWords = [...expandableWords].sort((a, b) => {
        return paragraph.indexOf(a.word) - paragraph.indexOf(b.word);
      });

      for (const item of sortedWords) {
        // Skip this word if it has already been processed
        if (processedWordsInThisRender.has(item.id)) continue;

        const wordIndex = paragraph.indexOf(item.word, lastIndex);
        if (wordIndex === -1) continue;

        // Add text before the expandable word
        if (wordIndex > lastIndex) {
          let textSegment = paragraph.substring(lastIndex, wordIndex);

          // If we need to skip punctuation from previous expanded word
          if (skipPunctuation) {
            // Skip any punctuation at the beginning and capitalize first letter
            textSegment = textSegment.replace(/^[,;:\s]+/, "");
            if (textSegment.length > 0) {
              textSegment =
                textSegment.charAt(0).toUpperCase() + textSegment.slice(1);
            }
            skipPunctuation = false;
          }

          result.push(
            <span key={`text-${paragraphIndex}-${lastIndex}`}>
              {textSegment}
            </span>
          );
        }

        // Check if the word is followed by a period
        const nextCharIndex = wordIndex + item.word.length;
        const hasPeriodAfter =
          nextCharIndex < paragraph.length && paragraph[nextCharIndex] === ".";

        // Adjust the displayed word to include the period if needed
        const displayWord = hasPeriodAfter ? `${item.word}.` : item.word;

        const isExpanded = expandedWords.includes(item.id);
        const dimensions = imageDimensions[item.id] || {
          width: 180,
          height: 120,
        };

        // Mark this word as processed
        processedWordsInThisRender.add(item.id);

        // Add the expandable word
        result.push(
          <span
            key={`word-${paragraphIndex}-${item.id}`}
            className="relative inline-flex items-center"
          >
            <button
              onClick={() => toggleWord(item.id)}
              className={cn(
                "inline-flex items-center transition-all text-[#333333] relative",
                isExpanded ? "text-black" : ""
              )}
            >
              <span className="underline font-normal">{displayWord}</span>
              {isExpanded && (
                <span className="ml-0.5 mb-[2px] text-[#333333] text-xl leading-none">
                  {!hasPeriodAfter && "."}
                </span>
              )}
            </button>
          </span>
        );

        // Add expanded content if expanded
        if (isExpanded) {
          // Split the content text by newlines to handle paragraphs within the expanded content
          const contentParagraphs = item.content.text
            .split("\n\n")
            .map((p, i) => (
              <div
                key={i}
                className={i > 0 ? "mt-3" : ""}
                dangerouslySetInnerHTML={{ __html: parseLinks(p) }}
              />
            ));

          result.push(
            <div
              key={`content-${paragraphIndex}-${item.id}`}
              className="w-full my-10 px-4 sm:px-8 py-4 transition-all duration-500 ease-in-out overflow-hidden rounded-lg"
              style={{
                opacity: 1,
                maxHeight: "none", // Allow unlimited height
                transform: "translateY(0)",
              }}
            >
              <div className="flex flex-col gap-6 items-start sm:flex-row sm:items-center">
                {item.content.imageUrl && (
                  <div
                    className="flex-shrink-0 w-full sm:w-auto flex justify-center sm:justify-start"
                    style={{
                      maxWidth: dimensions.width,
                      height: dimensions.height,
                    }}
                  >
                    <Image
                      src={item.content.imageUrl || "/placeholder.svg"}
                      alt={`Image for ${item.word}`}
                      width={dimensions.width}
                      height={dimensions.height}
                      className="rounded-md object-cover w-full h-auto sm:w-auto sm:h-auto"
                      priority
                    />
                  </div>
                )}
                <div className="flex-1 text-[#2a2a2a] flex flex-col justify-center font-light">
                  {contentParagraphs}
                </div>
              </div>
            </div>
          );

          // Set flag to skip punctuation after this word
          skipPunctuation = true;
        }

        // Update lastIndex to skip over the period if it was included
        lastIndex = wordIndex + item.word.length + (hasPeriodAfter ? 1 : 0);
      }

      // Add remaining text
      if (lastIndex < paragraph.length) {
        let textSegment = paragraph.substring(lastIndex);

        // If we need to skip punctuation from the last expanded word
        if (skipPunctuation) {
          // Skip any punctuation at the beginning and capitalize first letter
          textSegment = textSegment.replace(/^[,;:\s]+/, "");
          if (textSegment.length > 0) {
            textSegment =
              textSegment.charAt(0).toUpperCase() + textSegment.slice(1);
          }
        }

        result.push(
          <span key={`text-${paragraphIndex}-end`}>{textSegment}</span>
        );
      }

      // Use div with paragraph styling instead of p tag to avoid nesting issues
      return (
        <div
          key={`paragraph-${paragraphIndex}`}
          className={`${paragraphIndex > 0 ? "mt-4" : ""} mb-4`}
        >
          {result}
        </div>
      );
    });
  };

  return (
    <div className={cn("max-w-none text-lg", className)}>
      <div className="leading-relaxed text-[#212121] font-light">
        {renderText()}
      </div>
    </div>
  );
}
